import React, { useEffect, useRef, useState } from 'react';
import { Users, Globe, Star } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { familyTranslations } from '../data/translations';

const statIcons = [Users, Globe, Star];

const VisionSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  const { lang } = useLanguage();
  const t = familyTranslations[lang].visionData;

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-24 md:py-32 bg-white border-t border-gray-100">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid md:grid-cols-2 gap-16 items-start">
          {/* Left Content */}
          <div
            className={`transition-all duration-700 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#5B4FE9]">
              {t.label}
            </span>
            <h2
              className="mt-4 text-3xl sm:text-4xl md:text-[44px] font-bold text-gray-900 leading-[1.15] tracking-tight"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              {t.title},{' '}
              <span className="text-[#5B4FE9]">{t.titleHighlight}</span>
            </h2>
            <p className="mt-6 text-lg text-gray-500 leading-relaxed">
              {t.description}
            </p>

            {/* Mission box */}
            <div className="mt-8 p-6 rounded-2xl bg-gray-50 border border-gray-100">
              <h3 className="text-base font-bold text-gray-900">{t.mission.title}</h3>
              <p className="mt-2 text-sm text-gray-500 leading-relaxed">
                {t.mission.text}
              </p>
            </div>
          </div>

          {/* Right Stats */}
          <div className="space-y-4">
            {t.stats.map((stat, i) => {
              const Icon = statIcons[i];
              return (
                <div
                  key={i}
                  className={`p-6 rounded-2xl border border-gray-100 bg-white hover:shadow-md transition-all duration-500 ${
                    isVisible
                      ? 'opacity-100 translate-y-0'
                      : 'opacity-0 translate-y-6'
                  }`}
                  style={{ transitionDelay: `${300 + i * 150}ms` }}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-indigo-50 border border-indigo-100 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-[#5B4FE9]" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                      <p className="text-sm text-gray-500">{stat.label}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default VisionSection;
